import { Link } from 'react-router-dom';
import { ArrowRight, Export } from '@/components/icons';
import { Heading, Text } from '@/components/system';
import { cn } from '@/lib/utils';
import { HELP_TONE_STYLES } from './help-tone-styles';
import type { HelpShortcut } from '../types';

type HelpShortcutCardProps = {
    shortcut: HelpShortcut;
};

const cardClassName =
    'group flex h-full min-h-32 items-start gap-4 rounded-xl border border-border/80 bg-card p-4 text-left shadow-sm transition-[background-color,border-color,color] duration-150 ease-out hover:border-border-strong hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

const disabledCardClassName =
    'flex h-full min-h-32 items-start gap-4 rounded-xl border border-border/70 bg-card/70 p-4 text-left shadow-sm opacity-75';

function HelpShortcutContent({ shortcut }: { shortcut: HelpShortcut }) {
    const Icon = shortcut.icon;
    const isExternal = shortcut.action.type === 'external';
    const isDisabled = shortcut.action.type === 'disabled';
    const ActionIcon = isExternal ? Export : ArrowRight;
    const tone = HELP_TONE_STYLES[shortcut.tone];

    return (
        <>
            <span
                className={cn(
                    'flex size-11 shrink-0 items-center justify-center rounded-xl border transition-colors',
                    tone.icon,
                )}
            >
                <Icon className="size-5" />
            </span>

            <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    <Heading as="h3" variant="card">
                        {shortcut.title}
                    </Heading>
                    {shortcut.statusLabel ? (
                        <span
                            className={cn(
                                'rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.12em]',
                                tone.icon,
                            )}
                        >
                            {shortcut.statusLabel}
                        </span>
                    ) : null}
                </div>
                <Text variant="description">{shortcut.description}</Text>
            </div>

            {isDisabled ? null : (
                <ActionIcon className="mt-1 size-4 shrink-0 text-muted-foreground transition-[color,transform] duration-150 ease-out group-hover:translate-x-0.5 group-hover:text-foreground" />
            )}
        </>
    );
}

export function HelpShortcutCard({ shortcut }: HelpShortcutCardProps) {
    const { action } = shortcut;

    if (action.type === 'external') {
        return (
            <a href={action.href} target="_blank" rel="noreferrer" className={cardClassName}>
                <HelpShortcutContent shortcut={shortcut} />
            </a>
        );
    }

    if (action.type === 'disabled') {
        return (
            <article aria-disabled="true" className={disabledCardClassName}>
                <HelpShortcutContent shortcut={shortcut} />
            </article>
        );
    }

    return (
        <Link to={action.to} className={cardClassName}>
            <HelpShortcutContent shortcut={shortcut} />
        </Link>
    );
}
